import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {PropertyModel, ValuePropertyModel} from '../../../models/property.model';
import {PropertiesService} from '../../../services/api/properties-service.service';
import {AlertService} from '../../../services/notify/alert.service';
import {CdkDragDrop, moveItemInArray} from '@angular/cdk/drag-drop';
import {ModalService} from '../../../services/modal/modal.service';
import {TranslateService} from '@ngx-translate/core';

@Component({
    selector: 'app-property-option-table',
    templateUrl: './property-option-table.component.html',
    styleUrls: ['./property-option-table.component.scss']
})
export class PropertyOptionTableComponent implements OnInit, OnChanges {

    @Input() mode: 'board' | 'doc' | 'contract';
    @Input() targetProperties: ValuePropertyModel[] = [];

    @Output() saveOptionProperty: EventEmitter<ValuePropertyModel[]> = new EventEmitter<ValuePropertyModel[]>();

    keys: [
        'property_file',
        'property_member',
        'property_text',
        'property_boolean',
        'property_catalog',
        'property_todo',
    ] = [
        'property_file',
        'property_member',
        'property_text',
        'property_boolean',
        'property_catalog',
        'property_todo',
    ];

    propertyIcon: { [key: string]: { icon: string, tooltip: string } } = {
        property_file: {
            icon: 'bi bi-file-earmark-word prop_icon',
            tooltip: 'DOCUMENTS',
        },
        property_member: {
            icon: 'bi bi-person prop_icon',
            tooltip: 'MEMBER',
        },
        property_text: {
            icon: 'bbi bi-card-text prop_icon',
            tooltip: 'TEXT',
        },
        property_boolean: {
            icon: 'bi bi-check2-circle prop_icon',
            tooltip: 'BOOLEAN',
        },
        property_catalog: {
            icon: 'bi bi-justify prop_icon',
            tooltip: 'CATALOG',
        },
        property_todo: {
            icon: 'bi bi-clipboard2-check prop_icon',
            tooltip: 'TODO',
        },
    };

    propertyTextTemplate: { [key: string]: { key: string, isShow: boolean } } = {
        property_file: {
            key: 'DOCUMENTS',
            isShow: true,
        },
        property_member: {
            key: 'MEMBER',
            isShow: false,
        },
        property_text: {
            key: 'TEXT',
            isShow: false,
        },
        property_boolean: {
            key: 'BOOLEAN',
            isShow: false,
        },
        property_catalog: {
            key: 'CATALOG',
            isShow: false,
        },
        property_todo: {
            key: 'TODO',
            isShow: false,
        },
    };

    allProperties: {
        property_file?: PropertyModel[],
        property_member?: PropertyModel[],
        property_text?: PropertyModel[],
        property_boolean?: PropertyModel[],
        property_catalog?: PropertyModel[],
        property_todo?: PropertyModel[],
    } = {
        property_file: [],
        property_member: [],
        property_text: [],
        property_boolean: [],
        property_catalog: [],
        property_todo: [],
    };

    constructor(
        private propertiesService: PropertiesService,
        private alert: AlertService,
        private modalService: ModalService,
        public translate: TranslateService
    ) {
    }

    ngOnInit(): void {
        this.getAllProperties();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.targetProperties) {
            this.getAllProperties();
        }
    }

    getAllProperties(): void {
        this.propertiesService.getProperties(this.mode).subscribe({
            next: res => {
                this.allProperties = {
                    property_file: [],
                    property_member: [],
                    property_text: [],
                    property_boolean: [],
                    property_catalog: [],
                    property_todo: [],
                };

                res.properties.map((item: PropertyModel) => {
                    if (!this.targetProperties.filter((x: ValuePropertyModel) => x.property.id === item.id)[0]) {
                        this.allProperties[item.property_type].push(item);
                    }
                });
            }, error: err => {
                this.alert.setAlert(err.message, 'blue');
            }
        });
    }


    addProperty(changeProperty: PropertyModel): void {
        const modalSettings: any = {
            modalHeader: this.translate.instant('CONFIRM.HEADERS.ATTENTION'),
            modalBody: this.translate.instant('CONFIRM.TEXT.ADD_PROPERTY', {title: changeProperty.title}),
            buttonSubmit: this.translate.instant('CONFIRM.BUTTONS.YES'),
            buttonClose: this.translate.instant('CONFIRM.BUTTONS.NO'),
        };
        const modal = this.modalService.createFromSettings(modalSettings);
        modal.result.then(data => {
            if (data) {
                const pushProp: ValuePropertyModel = {
                    property: changeProperty,
                    default_view: true,
                };
                this.targetProperties.push(pushProp);

                this.allProperties[changeProperty.property_type] = this.allProperties[changeProperty.property_type]
                    .filter((x: PropertyModel) => x.id !== changeProperty.id);
                this.saveOptionProperty.emit(this.targetProperties);
            }
        }).catch(() => {
            return;
        });
    }

    changeView(prop: ValuePropertyModel): void {
        prop.default_view = !prop.default_view;

        this.saveOptionProperty.emit(this.targetProperties);
    }

    updateAfterDrag(e: CdkDragDrop<ValuePropertyModel>): void {
        if (e.previousIndex !== e.currentIndex) {
            moveItemInArray(this.targetProperties, e.previousIndex, e.currentIndex);
            this.saveOptionProperty.emit(this.targetProperties);
        }
    }

    deleteProp(prop: ValuePropertyModel): void {
        const modalSettings: any = {
            modalHeader: this.translate.instant('CONFIRM.HEADERS.ATTENTION'),
            modalBody: this.translate.instant('CONFIRM.TEXT.DELETE_FIELD', {title: prop.property.title}),
            buttonSubmit: this.translate.instant('CONFIRM.BUTTONS.YES'),
            buttonClose: this.translate.instant('CONFIRM.BUTTONS.NO'),
        };
        const modal = this.modalService.createFromSettings(modalSettings);
        modal.result.then(data => {
            if (data) {
                this.targetProperties = this.targetProperties.filter(x => x.property.id !== prop.property.id);
                this.saveOptionProperty.emit(this.targetProperties);
                this.getAllProperties();
            }
        }).catch(() => {
            return;
        });
    }
}
